import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import {
  ProbitChart,
  type Stream,
  type YAxisMode,
} from 'components/charts/probit-chart';
import { Dropdown, RadioButton, RadioButtonGroup } from '@carbon/react';
import styles from './probit-plots.module.scss';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import type { ColumnFiltersState } from '@tanstack/react-table';
import {
  probitPlotDataAtomFamily,
  fetchProbitPlotAtom,
  type ProbitMetric,
  type DcaParamKey,
  type WellSelectionState,
} from './atoms';
import { buildTableFilters } from './build-workspace-wells-query-payload';
import type { WellSelectionFilterMode } from './well-selection-filter';

const STREAMS: Stream[] = ['oil', 'gas', 'water'];

const STREAM_CFG: Record<Stream, { title: string; color: string }> = {
  oil: { title: 'Probit Plot - Oil', color: '#42be65' },
  gas: { title: 'Probit Plot - Gas', color: '#fa4d56' },
  water: { title: 'Probit Plot - Water', color: '#4589ff' },
};

type DropdownItem = { id: string; text: string };

const METRIC_ITEMS: DropdownItem[] = [
  { id: 'eur', text: 'EUR' },
  { id: 'eur_per_ft', text: 'EUR per ft' },
  // { id: 'scaled_eur', text: 'Scaled EUR' },
  { id: 'dca_params', text: 'DCA Parameters' },
];

const DCA_PARAM_ITEMS: DropdownItem[] = [
  { id: 'qi', text: 'qi' },
  { id: 'b_factor', text: 'b' },
  { id: 'di', text: 'di' },
];

const Y_MODE_ITEMS: DropdownItem[] = [
  { id: 'percentile', text: 'Percentile' },
  { id: 'probit', text: 'Probit' },
];

const METRIC_AXIS_LABELS: Record<ProbitMetric, string> = {
  eur: 'EUR',
  scaled_eur: 'Scaled EUR',
  eur_per_ft: 'EUR per ft',
  dca_params: 'DCA Parameters',
  cumulatives: 'Cumulatives',
};

const DCA_PARAM_LABELS: Record<DcaParamKey, string> = {
  qi: 'qi',
  b_factor: 'b',
  di: 'di',
};

const itemToString = (item: DropdownItem | null) => (item ? item.text : '');

// ---------------------------------------------------------------------------
// Per-stream chart card with its own metric state & data fetching
// ---------------------------------------------------------------------------

interface ProbitPlotCardProps {
  stream: Stream;
  workspaceId: number;
  dcaScenarioId: number;
  axisSwapped: boolean;
  wellFilter: WellSelectionFilterMode;
  wellsMap?: WellSelectionState;
  columnFilters?: ColumnFiltersState;
  globalFilter?: string;
}

const ProbitPlotCard: FC<ProbitPlotCardProps> = ({
  stream,
  workspaceId,
  dcaScenarioId,
  axisSwapped,
  wellFilter,
  wellsMap,
  columnFilters,
  globalFilter,
}) => {
  const [metric, setMetric] = useState<ProbitMetric>('eur');
  const [paramKey, setParamKey] = useState<DcaParamKey>('qi');
  const [yMode, setYMode] = useState<YAxisMode>('percentile');
  const [{ data, loading }] = useAtom(probitPlotDataAtomFamily(stream));
  const fetchData = useSetAtom(fetchProbitPlotAtom);
  const initialPollDoneRef = useRef(false);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollStartRef = useRef<number>(0);

  const wellFilterPayload = useMemo<{
    uwis?: string[];
    excludeUwis?: string[];
    wellsWithSpecificVersionIds?: { uwi: string; wellDcaForecastId: number }[];
  }>(() => {
    if (!wellsMap) return {};

    const specificVersions: { uwi: string; wellDcaForecastId: number }[] = [];
    for (const [uwi, entry] of Object.entries(wellsMap.items)) {
      if (entry.selected && !entry.isLatestForecast && entry.dcaForecastId) {
        specificVersions.push({
          uwi,
          wellDcaForecastId: Number(entry.dcaForecastId),
        });
      }
    }

    const result: typeof specificVersions extends never
      ? never
      : {
          uwis?: string[];
          excludeUwis?: string[];
          wellsWithSpecificVersionIds?: {
            uwi: string;
            wellDcaForecastId: number;
          }[];
        } = {};

    if (specificVersions.length > 0) {
      result.wellsWithSpecificVersionIds = specificVersions;
    }

    if (wellFilter === 'all') return result;

    if (wellFilter === 'selected') {
      if (wellsMap.mode === 'all_selected') {
        const excluded = Object.keys(wellsMap.items).filter(
          (uwi) => !wellsMap.items[uwi].selected
        );
        if (excluded.length > 0) result.excludeUwis = excluded;
      } else {
        const included = Object.keys(wellsMap.items).filter(
          (uwi) => wellsMap.items[uwi].selected
        );
        result.uwis = included;
      }
    } else {
      if (wellsMap.mode === 'all_selected') {
        const unselected = Object.keys(wellsMap.items).filter(
          (uwi) => !wellsMap.items[uwi].selected
        );
        result.uwis = unselected;
      } else {
        const selected = Object.keys(wellsMap.items).filter(
          (uwi) => wellsMap.items[uwi].selected
        );
        if (selected.length > 0) result.excludeUwis = selected;
      }
    }

    return result;
  }, [wellFilter, wellsMap]);

  const tableFilters = useMemo<Record<string, unknown>>(
    () => buildTableFilters(columnFilters ?? [], globalFilter ?? ''),
    [columnFilters, globalFilter]
  );

  const doFetch = useCallback(() => {
    fetchData({
      stream,
      workspaceId,
      dcaScenarioId,
      metric,
      paramKey: metric === 'dca_params' ? paramKey : undefined,
      ...wellFilterPayload,
      filters: tableFilters,
    });
  }, [
    stream,
    workspaceId,
    dcaScenarioId,
    metric,
    paramKey,
    wellFilterPayload,
    tableFilters,
    fetchData,
  ]);

  // Initial page-load polling: fetch every 5s for up to 60s
  useEffect(() => {
    if (initialPollDoneRef.current) return;

    doFetch();
    pollStartRef.current = Date.now();

    pollTimerRef.current = setInterval(() => {
      if (Date.now() - pollStartRef.current >= 60_000) {
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
        initialPollDoneRef.current = true;
        return;
      }
      doFetch();
    }, 5_000);

    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    };
    // Only run once on mount — intentionally excluding doFetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Subsequent fetches when params change (after initial poll or during it)
  useEffect(() => {
    if (!initialPollDoneRef.current) return;
    doFetch();
  }, [doFetch]);

  const xAxisLabel = useMemo(() => {
    const unit = data?.streams[stream]?.unit;
    const base =
      metric === 'dca_params'
        ? DCA_PARAM_LABELS[paramKey]
        : METRIC_AXIS_LABELS[metric];
    return unit ? `${base} [${unit}]` : base;
  }, [metric, paramKey, data, stream]);

  const header = (
    <div className={styles.chartTitleRow}>
      <div className={styles.chartTitle}>{STREAM_CFG[stream].title}</div>
    </div>
  );

  const footer = (
    <div className={styles.chartFooter}>
      <div className={styles.footerLeft}>
        <span className={styles.footerLabel}>X-axis:</span>
        <Dropdown
          id={`metric-${stream}`}
          size="sm"
          direction="top"
          titleText=""
          label="Select Parameter"
          items={METRIC_ITEMS}
          itemToString={itemToString}
          selectedItem={METRIC_ITEMS.find((i) => i.id === metric) ?? null}
          onChange={({ selectedItem }) => {
            if (selectedItem) setMetric(selectedItem.id as ProbitMetric);
          }}
          className={styles.carbonDropdown}
          disabled={loading}
        />

        {metric === 'dca_params' && (
          <Dropdown
            id={`dca-param-${stream}`}
            size="sm"
            direction="top"
            titleText=""
            label="Select Param"
            items={DCA_PARAM_ITEMS}
            itemToString={itemToString}
            selectedItem={
              DCA_PARAM_ITEMS.find((i) => i.id === paramKey) ?? null
            }
            onChange={({ selectedItem }) => {
              if (selectedItem) setParamKey(selectedItem.id as DcaParamKey);
            }}
            className={styles.carbonDropdown}
            disabled={loading}
          />
        )}
      </div>

      <div className={styles.footerRight}>
        <span className={styles.footerLabel}>Y-axis:</span>
        <Dropdown
          id={`y-mode-${stream}`}
          size="sm"
          direction="top"
          titleText=""
          label="Select Y-Axis"
          items={Y_MODE_ITEMS}
          itemToString={itemToString}
          selectedItem={Y_MODE_ITEMS.find((i) => i.id === yMode) ?? null}
          onChange={({ selectedItem }) => {
            if (selectedItem) setYMode(selectedItem.id as YAxisMode);
          }}
          className={styles.carbonDropdown}
          disabled={loading}
        />
      </div>
    </div>
  );

  return (
    <div className={styles.chartWrapper}>
      <ProbitChart
        data={data?.streams[stream] ?? null}
        uwis={data?.uwis ?? []}
        yMode={yMode}
        axisSwapped={axisSwapped}
        color={STREAM_CFG[stream].color}
        xAxisLabel={xAxisLabel}
        loading={loading}
        header={header}
        footer={footer}
        exportConfig={{
          label: STREAM_CFG[stream].title,
          filenamePrefix: `dca-probit-${stream}`,
        }}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Parent section
// ---------------------------------------------------------------------------

interface ProbitPlotsProps {
  workspaceId?: number;
  dcaScenarioId?: number;
  wellsMap?: WellSelectionState;
  columnFilters?: ColumnFiltersState;
  globalFilter?: string;
}

export const ProbitPlots: FC<ProbitPlotsProps> = ({
  workspaceId,
  dcaScenarioId,
  wellsMap,
  columnFilters,
  globalFilter,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [wellFilter, setWellFilter] = useState<WellSelectionFilterMode>('all');

  const ready = workspaceId !== undefined && dcaScenarioId !== undefined;

  return (
    <Accordion expanded={expanded} onClick={() => setExpanded((prev) => !prev)}>
      <AccordionSummary>
        <h2 className={styles.sectionHeading}>Probit Plots</h2>
      </AccordionSummary>
      <AccordionDetails>
        <div className={styles.probitPlotsContainer}>
          <div className={styles.wellFilterBar}>
            <RadioButtonGroup
              legendText=""
              name="probit-well-filter"
              orientation="horizontal"
              valueSelected={wellFilter}
              onChange={(value: string | number | undefined) =>
                setWellFilter(value as WellSelectionFilterMode)
              }
            >
              <RadioButton labelText="All" value="all" id="probit-wf-all" />
              <RadioButton
                labelText="Selected"
                value="selected"
                id="probit-wf-selected"
              />
              <RadioButton
                labelText="Not Selected"
                value="not_selected"
                id="probit-wf-not-selected"
              />
            </RadioButtonGroup>
          </div>
          <div className={styles.chartsGrid}>
            {ready &&
              STREAMS.map((stream) => (
                <ProbitPlotCard
                  key={stream}
                  stream={stream}
                  workspaceId={workspaceId}
                  dcaScenarioId={dcaScenarioId}
                  axisSwapped={false}
                  wellFilter={wellFilter}
                  wellsMap={wellsMap}
                  columnFilters={columnFilters}
                  globalFilter={globalFilter}
                />
              ))}
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};
